import Grid from "@bmi-digital/components/grid";
import NBACard from "@bmi-digital/components/nba-card";
import Section from "@bmi-digital/components/section";
import { replaceSpaces, transformHyphens } from "@bmi-digital/components/utils";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { graphql } from "gatsby";
import React from "react";
import { useSiteContext } from "../Site";
import { getCTA } from "../link/utils";
import type { PageInfoCard } from "../PageInfo";
import type { PromoCard } from "../Promo";
import type { DarkColor } from "@bmi-digital/components/color-pair";

export type NextBestActionsData = PromoCard | PageInfoCard;

const indexToBackgroundMap: DarkColor[] = [
  "secondary1",
  "secondary2",
  "secondary3",
  "secondary4"
];

const getTitle = (data: NextBestActionsData): string => {
  if (data.collectionType === "promo") {
    return transformHyphens(data.title || data.name);
  }
  return transformHyphens(data.title);
};

const NextBestActions = ({
  data
}: {
  data: readonly { value: NextBestActionsData }[];
}) => {
  const { getMicroCopy, countryCode } = useSiteContext();
  return (
    <Section
      backgroundColor="alabaster"
      data-testid="next-best-actions-section"
    >
      <Section.Title>{getMicroCopy(microCopy.NBA_TITLE)}</Section.Title>
      <Grid container spacing={3}>
        {data.map(({ value: cardData }, index) => {
          const transformedTitle = getTitle(cardData);
          const cta = getCTA(cardData, countryCode, transformedTitle);
          const ctaLabel =
            cardData.collectionType === "promo"
              ? cardData.cta?.[0]?.label
              : transformedTitle;

          return (
            <Grid xs={12} md={4} lg={3} key={`nba-${index}`}>
              <NBACard
                // eslint-disable-next-line security/detect-object-injection
                color={indexToBackgroundMap[index]}
                title={transformedTitle}
                description={transformHyphens(
                  "subtitle" in cardData ? cardData.subtitle : ""
                )} // TODO: DXB-7055 Description can't be null
                ctaLabel={ctaLabel}
                data-testid={`nba-card-${replaceSpaces(transformedTitle)}`}
                {...cta} // TODO: DXB-7055 CTA can't be null
              />
            </Grid>
          );
        })}
      </Grid>
    </Section>
  );
};

export default NextBestActions;

export const query = graphql`
  fragment NextBestActionsFragment on PayloadPromoOrPageValue {
    ... on PayloadPromo {
      ...PromoCardFragment
    }
    ... on PayloadPage {
      ...PageInfoCardFragment
    }
  }
`;
